import { Injectable } from '@angular/core';

/**
 * Copy text to the clipboard.
 *
 * Uses the async Clipboard API, which the Tauri v2 webview supports because
 * `tauri://localhost` counts as a secure context — so no Tauri clipboard plugin
 * (and no Rust-side capability) is needed. The `execCommand` path covers the
 * cases where the API is absent or refused, such as a non-secure origin.
 */
@Injectable({ providedIn: 'root' })
export class ClipboardService {
  async copy(text: string): Promise<boolean> {
    if (!text) return false;

    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
        return true;
      }
    } catch (e) {
      console.warn('[Clipboard] Clipboard API refused, falling back', e);
    }

    return this.copyViaTextarea(text);
  }

  /** Legacy path: a hidden textarea plus `execCommand('copy')`. */
  private copyViaTextarea(text: string): boolean {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    // Keep it out of view without making it unfocusable, which would break the copy.
    textarea.setAttribute('readonly', '');
    textarea.style.position = 'fixed';
    textarea.style.top = '-1000px';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);

    try {
      textarea.select();
      return document.execCommand('copy');
    } catch (e) {
      console.error('[Clipboard] Copy failed', e);
      return false;
    } finally {
      document.body.removeChild(textarea);
    }
  }
}
